// Pure, testable iOS detection for the dynamic install prompt.
//
// Design decision: the dynamic prompt is the SINGLE install mechanism on iOS -
// we do NOT also use Apple's native Smart App Banner. Running both would force a
// "is this Safari?" check to avoid a double prompt, and that check is impossible
// to do reliably: a generic in-app WKWebView (Reddit and others) is byte-for-byte
// indistinguishable from real Safari by user-agent, and `navigator.standalone`
// is not a dependable WKWebView signal. So instead of guessing, we show the one
// dynamic prompt in EVERY iOS browser (Safari, Chrome, Firefox, and the
// Facebook / Instagram / Reddit in-app webviews - the highest-value placements,
// where the native banner never rendered anyway). The only gates are: don't
// prompt inside an already-installed standalone PWA, and respect dismissal.
//
// The same banner (same slot, same dismissal) offers Google Play to Android
// browsers: installPromptPlatform below. Android has no such double-prompt
// problem to design around; we simply never prompt inside our own app.

import { isAndroidApp } from './androidApp'

/**
 * localStorage key for "the visitor dismissed the install banner", on either
 * platform (the value predates the Android banner; renaming it would bring
 * back the banner for every iPhone that already dismissed it). Read by
 * IosInstallPrompt AND by the inline script the prerender emits before #root
 * (scripts/prerender-guard-lib.mjs, iosSlotScript), which reserves the
 * banner's space before first paint so mounting it shifts nothing. That
 * script restates installPromptPlatform in ES5; the two are pinned to
 * each other across a UA matrix by scripts/__tests__/ios-slot-script.test.mjs.
 */
export const IOS_PROMPT_DISMISS_KEY = 'cb_ios_prompt_dismissed'

/**
 * On a prerendered page that inline script also records its decision on
 * <html> as this attribute ('1' show, '0' hide). The banner must follow the
 * slot the script reserved: re-checking later (after the preload and the
 * language load) could disagree, e.g. when another tab dismissed the banner
 * in between, and the page would shift by the banner's height.
 */
export const IOS_BANNER_DECISION_ATTR = 'data-cb-ios-banner'

/**
 * The decision the prerendered page's inline script recorded, or null where
 * there is none (every page that is not prerendered, and SSR/tests).
 */
export function readRecordedIosBannerDecision(): boolean | null {
  if (typeof document === 'undefined') return null
  const v = document.documentElement.getAttribute(IOS_BANNER_DECISION_ATTR)
  if (v === '1') return true
  if (v === '0') return false
  return null
}

export interface UaEnv {
  ua: string
  platform: string
  maxTouchPoints: number
  /** True when running as an installed standalone PWA (don't prompt). */
  standalone: boolean
  /**
   * True inside our Android app (src/lib/androidApp.ts). Never offer the
   * iPhone app there. Optional so a caller that only has a UA still works.
   */
  androidApp?: boolean
}

export function isIos({
  ua,
  platform,
  maxTouchPoints,
}: Pick<UaEnv, 'ua' | 'platform' | 'maxTouchPoints'>): boolean {
  if (/iPhone|iPod|iPad/.test(ua)) return true
  // iPadOS 13+ reports as desktop Mac Safari; the touch-point count gives it away.
  if (platform === 'MacIntel' && maxTouchPoints > 1) return true
  return false
}

/**
 * Whether the install banner offers the iPhone app: on iOS, in any browser,
 * unless already running as an installed standalone PWA. No browser/Safari
 * detection - see the file header for why that would be unreliable.
 */
export function shouldShowIosInstallPrompt(env: UaEnv): boolean {
  return installPromptPlatform(env) === 'ios'
}

/** An Android device's browser UA (Chrome, Samsung Internet, Firefox, webviews). */
export function isAndroidUa({ ua }: Pick<UaEnv, 'ua'>): boolean {
  return /Android/.test(ua)
}

export type InstallPromptPlatform = 'ios' | 'android'

/**
 * Which store the install banner offers, or null for no banner. ONE banner,
 * ONE slot, ONE dismissal for both platforms (a device is only ever one of
 * the two, and the prerendered slot is the same box either way):
 *   - 'ios' on an iPhone, iPod or iPad, in any browser (the file header);
 *   - 'android' on an Android browser, which links to Google Play;
 *   - null in an installed standalone PWA, inside our Android app (a TWA,
 *     src/lib/androidApp.ts, whose Chrome UA says Android), and everywhere
 *     else.
 * iOS is checked first; no real UA matches both.
 * The prerender's inline slot script (iosSlotScript) restates this in ES5.
 */
export function installPromptPlatform(env: UaEnv): InstallPromptPlatform | null {
  if (env.standalone || env.androidApp) return null
  /* eslint-disable i18next/no-literal-string -- platform ids, never shown */
  if (isIos(env)) return 'ios'
  if (isAndroidUa(env)) return 'android'
  /* eslint-enable i18next/no-literal-string */
  return null
}

/** Read the live browser environment (guards SSR / jsdom). */
export function readUaEnv(): UaEnv {
  if (typeof navigator === 'undefined') {
    return { ua: '', platform: '', maxTouchPoints: 0, standalone: false, androidApp: false }
  }
  const nav = navigator as Navigator & { standalone?: boolean }
  const standalone =
    nav.standalone === true ||
    (typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches)
  return {
    ua: navigator.userAgent || '',
    platform: navigator.platform || '',
    maxTouchPoints: navigator.maxTouchPoints || 0,
    standalone,
    androidApp: isAndroidApp(),
  }
}
